import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, googleExchange, appleUnlink } from '../utils/api';
import { useGoogleLogin } from '@react-oauth/google';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { ArrowLeft, User, Mail, Phone, Shield, HelpCircle, Save, Camera, CheckCircle, KeyRound, AlertTriangle, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import OtpResendButton from '../components/OtpResendButton';
import AvatarCropModal from '../components/AvatarCropModal';
import { getInitials, getAvatarColor } from '../utils/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditProfile = () => {
  const { user, loading: authLoading, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [emailOtp, setEmailOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [unlinkingApple, setUnlinkingApple] = useState(false);
  const [previewPic, setPreviewPic] = useState(null);
  // iter95 — pending-crop state. When the user picks a file, we hold it here
  // and render the crop modal. Only after they confirm the crop do we
  // actually upload the compressed result to the backend.
  const [cropFile, setCropFile] = useState(null);
  // ── Account deletion state ───────────────────────────────────────────
  // Two-step confirmation: open modal → user types DELETE → click Delete.
  // Prevents accidental taps (required for App Store Guideline 5.1.1(v)).
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Link Google account to the currently logged-in user (Scenario B). Custom Google OAuth
  // using GenzoX's own client — popup flow returns auth code, backend merges by email.
  const handleLinkGoogle = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    onSuccess: async (codeResponse) => {
      setLinkingGoogle(true);
      try {
        // iter131 — pass link:true so backend enters the linking branch.
        // Without this flag the endpoint now treats the request as a fresh
        // sign-in (which would just log the user out of their account).
        await googleExchange(codeResponse.code, 'postmessage', { link: true });
        toast.success('Google account linked!');
        await checkAuth();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to link Google');
      } finally {
        setLinkingGoogle(false);
      }
    },
    onError: () => { toast.error('Google link cancelled'); },
  });

  // iter71 — Redirect must be in a useEffect (not during render). Placed here
  // BEFORE any early-return so React's hook count stays stable across renders.
  // (The previous attempt put this after `if (authLoading) return` which made
  // hook count grow when authLoading flipped → Rules-of-Hooks violation.)
  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [authLoading, user, navigate]);

  // Wait for auth to finish loading before deciding to redirect. Previously the
  // page synchronously redirected to /login on every mount because `user` was
  // still null while /api/auth/me was in flight — causing the "My Account
  // sometimes fails to load" symptom on slower networks.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="profile-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#1800ad]"></div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="profile-redirecting">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#1800ad]"></div>
      </div>
    );
  }

  const isEmailDirty = email.trim().toLowerCase() !== (user.email || '').toLowerCase();
  const emailVerified = !!user.email_verified && !isEmailDirty;

  const getPictureUrl = () => {
    if (previewPic) return previewPic;
    if (!user.picture) return null;
    if (user.picture.startsWith('http')) return user.picture;
    return `${API_URL}${user.picture}`;
  };

  const handlePictureUpload = (e) => {
    const file = e.target.files?.[0];
    // Reset the input value so picking the same file again still fires
    // onChange (browsers dedupe by name+lastModified otherwise).
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    // Open the crop modal — actual upload happens in handleCropConfirm
    // after compression brings the payload down to ~150KB.
    setCropFile(file);
  };

  const handleCropConfirm = async (croppedFile) => {
    setCropFile(null);
    if (!croppedFile) { toast.error('Failed to process image'); return; }
    setPreviewPic(URL.createObjectURL(croppedFile));
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedFile);
      const res = await fetch(`${API_URL}/api/auth/upload-profile-picture`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      if (res.ok) { await checkAuth(); toast.success('Profile picture updated!'); }
      else { toast.error('Failed to upload'); setPreviewPic(null); }
    } catch (_e) { toast.error('Upload failed'); setPreviewPic(null); }
    finally { setUploading(false); }
  };

  const handleSaveName = async () => {
    if (!name.trim() || name === user.name) { toast.info('No name changes to save'); return; }
    setSaving(true);
    try {
      await authAPI.updateProfile({ name: name.trim() });
      toast.success('Name updated!');
      await checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleSendEmailOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) { toast.error('Enter a valid email'); return; }
    setSendingOtp(true);
    try {
      await authAPI.sendEmailOTP(trimmed);
      setShowOtpInput(true);
      toast.success('Verification code sent to ' + trimmed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send code');
    } finally { setSendingOtp(false); }
  };

  const handleVerifyEmailOtp = async () => {
    if (emailOtp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setVerifyingOtp(true);
    try {
      await authAPI.verifyEmailOTP(email.trim().toLowerCase(), emailOtp);
      toast.success('Email verified!');
      setShowOtpInput(false);
      setEmailOtp('');
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setVerifyingOtp(false); }
  };

  // ── Permanent account deletion (App Store Guideline 5.1.1(v)) ─────────
  // Runs AFTER the user has (a) opened the modal, (b) read the consequences
  // list, and (c) typed DELETE to confirm. The backend cascades cleanup of
  // all owned data and invalidates every session. We then locally clear
  // auth and navigate home.
  const handleConfirmDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeletingAccount(true);
    try {
      await authAPI.deleteAccount();
      toast.success('Your account has been permanently deleted.', { duration: 6000 });
      // Best-effort logout — backend already deleted the session, but this
      // also clears the OneSignal external_id + native Google session.
      try { await logout(); } catch (_e) { /* server-side already gone */ }
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete account. Please try again.');
      setDeletingAccount(false);
    }
  };

  const closeDeleteModal = () => {
    if (deletingAccount) return; // can't cancel mid-deletion
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  const picUrl = getPictureUrl();

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 sm:py-8">
      <div className="max-w-2xl mx-auto px-3 sm:px-6">
        <Link to="/dashboard" className="text-[#6B7280] hover:text-[#1800ad] text-xs sm:text-sm font-medium mb-4 inline-block" data-testid="back-dashboard">&larr; Back to Dashboard</Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0B10] mb-6" style={{ fontFamily: 'Google Sans Flex' }} data-testid="profile-title">My Account</h1>

        <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100 mb-4" data-testid="profile-section">
          {/* Profile Picture */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()}>
              {picUrl ? (
                <img src={picUrl} alt={user.name} className="w-20 h-20 rounded-full border-3 border-[#1800ad]/20 object-cover" referrerPolicy="no-referrer" />
              ) : (
                // iter95 — initials avatar (deterministic colour hashed from user id/name).
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ background: getAvatarColor(user.user_id || user.name) }}
                >
                  {getInitials(user.name)}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera size={20} className="text-white" />
              </div>
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-white/80 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#1800ad]"></div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePictureUpload} className="hidden" data-testid="picture-upload" />
            </div>
            <button onClick={() => fileRef.current?.click()} className="text-[#1800ad] text-xs font-bold mt-2 hover:underline" data-testid="change-picture-btn">
              {picUrl ? 'Change Photo' : 'Add Photo'}
            </button>
            <h2 className="text-lg font-bold text-[#0A0B10] mt-2">{user.name}</h2>
            <p className="text-xs text-[#9CA3AF]">Member since {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : 'Recently'}</p>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><User size={14} /> Name</label>
              <div className="flex gap-2">
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name"
                  className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" data-testid="name-input" />
                <button onClick={handleSaveName} disabled={saving || !name.trim() || name === user.name} data-testid="save-name-btn"
                  className="px-4 rounded-xl bg-[#1800ad] text-white text-xs font-bold hover:bg-[#1200a0] disabled:opacity-40">
                  {saving ? '...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Phone size={14} /> Phone</label>
              <input value={user.phone || 'Not set'} disabled
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-[#6B7280] cursor-not-allowed" data-testid="phone-input" />
              <p className="text-[10px] text-[#9CA3AF] mt-1">Phone cannot be changed for security. To add a phone, post an ad.</p>
            </div>

            {/* Email with verification */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5">
                <Mail size={14} /> Email
                {emailVerified && (
                  <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700" data-testid="email-verified-badge">
                    <CheckCircle size={10} /> VERIFIED
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <input value={email} onChange={e => { setEmail(e.target.value); setShowOtpInput(false); setEmailOtp(''); }} type="email" placeholder="your@email.com"
                  className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" data-testid="email-input" />
                {!emailVerified && (
                  <button onClick={handleSendEmailOtp} disabled={sendingOtp || !email.trim() || !email.includes('@')} data-testid="send-email-otp-btn"
                    className="px-4 rounded-xl bg-[#ff8500] text-white text-xs font-bold hover:bg-[#e57500] disabled:opacity-40 whitespace-nowrap">
                    {sendingOtp ? '...' : (showOtpInput ? 'Resend' : 'Send Code')}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-1">
                {emailVerified ? 'Your email is verified.' : (user.email ? 'Update your email — a verification code will be sent.' : 'Add your email and verify it to receive notifications.')}
              </p>
              {showOtpInput && (
                <div className="mt-3 p-3 rounded-xl bg-[#fff4ed] border border-[#ff8500]/20">
                  <label className="flex items-center gap-1.5 text-[11px] font-bold text-[#0A0B10] mb-1.5"><KeyRound size={12} /> Enter 6-digit code from your email</label>
                  <div className="flex gap-2">
                    <input value={emailOtp} onChange={e => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="------" maxLength={6} autoFocus data-testid="email-otp-input"
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-center tracking-[0.5em] font-bold text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                    <button onClick={handleVerifyEmailOtp} disabled={verifyingOtp || emailOtp.length !== 6} data-testid="verify-email-otp-btn"
                      className="px-4 rounded-lg bg-[#1800ad] text-white text-xs font-bold hover:bg-[#1200a0] disabled:opacity-40">
                      {verifyingOtp ? '...' : 'Verify'}
                    </button>
                  </div>
                  {/* iter65 — Resend email code with 30s countdown */}
                  <div className="mt-2 flex items-center justify-end">
                    <OtpResendButton
                      onResend={async () => {
                        try {
                          await authAPI.sendEmailOTP(email.trim());
                          toast.success('Code resent to your email');
                        } catch (e) {
                          const detail = e?.response?.data?.detail;
                          toast.error(detail || 'Failed to resend code. Try again.');
                          throw e;
                        }
                      }}
                      resendLabel="Resend code"
                      testIdPrefix="email-resend"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Verification */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Shield size={14} /> Status</label>
              <div className="flex gap-2 flex-wrap">
                {user.phone_verified && <span className="px-3 py-1.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200" data-testid="badge-phone-verified">Phone Verified</span>}
                {user.email_verified && <span className="px-3 py-1.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200" data-testid="badge-email-verified">Email Verified</span>}
                {!user.phone_verified && !user.email_verified && <span className="px-3 py-1.5 rounded-full text-[10px] font-bold bg-gray-100 text-[#6B7280]">Not Verified</span>}
                {user.is_admin && <span className="px-3 py-1.5 rounded-full text-[10px] font-bold bg-[#f0edff] text-[#1800ad]">Admin</span>}
              </div>
            </div>
          </div>

          {/* Link Google Account (only if email is NOT yet linked) */}
          {!user.email_verified && (
            <button onClick={() => handleLinkGoogle()} disabled={linkingGoogle} data-testid="link-google-btn"
              className="mt-4 w-full bg-white border-2 border-gray-200 text-[#0A0B10] rounded-xl py-3 font-bold text-sm hover:border-[#1800ad] hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              <FcGoogle size={20} /> {linkingGoogle ? 'Linking...' : 'Link Google Account'}
            </button>
          )}

          {/* iter131 — Disconnect Apple Sign-In (recovery path). Visible only
               when the current user has an Apple sub linked. Anyone impacted
               by the pre-iter131 silent-link bug can click this once, then
               sign back in with Apple to get a fresh, correct account. */}
          {(user.apple_sub || (Array.isArray(user.linked_apple_subs) && user.linked_apple_subs.length > 0)) && (
            <button
              onClick={async () => {
                if (unlinkingApple) return;
                if (!window.confirm('Disconnect Sign in with Apple from this account? You can sign in with Apple again to create a fresh account.')) return;
                setUnlinkingApple(true);
                try {
                  const r = await appleUnlink();
                  toast.success(r?.data?.unlinked_count > 0 ? 'Apple Sign-In disconnected.' : 'No Apple link was set.');
                  await checkAuth();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to disconnect Apple');
                } finally {
                  setUnlinkingApple(false);
                }
              }}
              disabled={unlinkingApple}
              data-testid="unlink-apple-btn"
              className="mt-3 w-full bg-white border-2 border-gray-200 text-[#0A0B10] rounded-xl py-3 font-bold text-sm hover:border-red-500 hover:text-red-600 hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <FaApple size={18} /> {unlinkingApple ? 'Disconnecting…' : 'Disconnect Apple Sign-In'}
            </button>
          )}
        </div>

        {/* Help */}
        <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100" data-testid="help-section">
          <h3 className="flex items-center gap-2 text-lg font-bold text-[#0A0B10] mb-4"><HelpCircle size={18} /> Need Help?</h3>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="font-bold text-[#0A0B10] text-sm mb-1">Change Phone Number?</p>
              <p className="text-xs text-[#4B5563]">Contact our support team for phone number changes.</p>
            </div>
            <div className="p-3 bg-red-50 rounded-xl border border-red-100" data-testid="delete-account-row">
              <p className="font-bold text-[#0A0B10] text-sm mb-1 flex items-center gap-1.5">
                <Trash2 size={14} className="text-red-600" /> Delete Account
              </p>
              <p className="text-xs text-[#4B5563] mb-3">
                Permanently delete your GenzoX account and all associated data.
                This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                data-testid="open-delete-account-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs font-bold transition-colors"
              >
                <Trash2 size={14} /> Delete My Account
              </button>
            </div>
            <div className="mt-4 text-center">
              <a href="mailto:help@genzox.in" className="text-[#1800ad] text-sm font-bold hover:underline" data-testid="contact-support">
                Contact: help@genzox.in
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Account Deletion Confirmation Modal ────────────────────────
          App Store Guideline 5.1.1(v) — requires explicit confirmation
          before permanent deletion. Two-step UX:
            1. Read the consequences list
            2. Type DELETE to enable the button */}
      {cropFile && (
        <AvatarCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
          data-testid="delete-account-modal"
          onClick={closeDeleteModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <h2 id="delete-account-title" className="text-lg font-bold text-[#0A0B10]">
                    Delete your account?
                  </h2>
                </div>
                <button
                  onClick={closeDeleteModal}
                  disabled={deletingAccount}
                  data-testid="close-delete-modal-btn"
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-[#4B5563] mb-3">
                This will <strong>permanently</strong> delete your GenzoX account.
                You will lose access to:
              </p>
              <ul className="space-y-1.5 mb-4 text-sm text-[#4B5563]">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>All your <strong>active listings</strong> will be removed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>Your entire <strong>chat history</strong> will no longer be accessible</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>Your <strong>wishlist</strong> and saved preferences will be removed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>Any active <strong>subscription or boosted ads</strong> will be cancelled</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>This action <strong>cannot be undone</strong></span>
                </li>
              </ul>

              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <label htmlFor="delete-confirm-input" className="block text-xs font-bold text-red-700 mb-2">
                  Type <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-red-200">DELETE</span> to confirm
                </label>
                <input
                  id="delete-confirm-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                  autoComplete="off"
                  disabled={deletingAccount}
                  className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 bg-white font-mono tracking-wider uppercase disabled:opacity-60"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={closeDeleteModal}
                  disabled={deletingAccount}
                  data-testid="cancel-delete-account-btn"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-[#0A0B10] text-sm font-bold transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteAccount}
                  disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                  data-testid="confirm-delete-account-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 size={15} /> {deletingAccount ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
